import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Drawer, Button, Dropdown, message, Input, InputNumber, Divider, Segmented } from 'antd';
import {
    CloseOutlined, DownloadOutlined, SettingOutlined,
    FontSizeOutlined, AppstoreOutlined, EditOutlined, EyeOutlined, SaveOutlined,
    FolderOpenOutlined, DeleteOutlined,
} from '@ant-design/icons';
import GlyphLibraryView from './glyph-editor/GlyphLibraryView';
import SentencePreviewView from './glyph-editor/SentencePreviewView';
import GlyphBezierEditor from './glyph-editor/GlyphBezierEditor';
import { generateDefaultGlyphs, DEFAULT_METADATA } from './glyph-editor/glyphEditorState';
import api from '../../../api/client';

/**
 * GlyphEditorPanel - Fullscreen drawer for creating fonts from scratch.
 * Three views: Glyph Library, Sentence Preview, Glyph Bezier Editor.
 */
const GlyphEditorPanel = ({ open, onClose, canvasId }) => {
    // Font state
    const [metadata, setMetadata] = useState({ ...DEFAULT_METADATA });
    const [glyphs, setGlyphs] = useState(() => generateDefaultGlyphs());
    const [accents, setAccents] = useState([]);

    // UI state
    const [activeTab, setActiveTab] = useState('library');
    const [editingGlyph, setEditingGlyph] = useState(null);
    const [selectedGlyph, setSelectedGlyph] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [exporting, setExporting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedProjects, setSavedProjects] = useState([]);
    const [lastSaved, setLastSaved] = useState(null);
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const autoSaveTimer = useRef(null);

    // --- Load saved font families from API ---
    const loadSavedProjects = useCallback(async () => {
        try {
            const res = await api.get('/font-families', { params: canvasId ? { canvasId } : {} });
            setSavedProjects(res.data || []);
            return res.data || [];
        } catch (e) {
            console.warn('Failed to load font families:', e);
            setSavedProjects([]);
            return [];
        }
    }, [canvasId]);

    // Load most recent project for this canvas on mount
    useEffect(() => {
        if (!open) return;
        (async () => {
            const projects = await loadSavedProjects();
            // Auto-load the most recently updated project for this canvas
            if (projects.length > 0 && !currentProjectId) {
                const sorted = [...projects].sort((a, b) =>
                    new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
                );
                const latest = sorted[0];
                if (latest.metadata) setMetadata(latest.metadata);
                if (latest.glyphs?.length) setGlyphs(latest.glyphs);
                if (latest.accents) setAccents(latest.accents);
                setCurrentProjectId(latest.id);
                setLastSaved(new Date(latest.updated_at || latest.created_at));
            }
        })();
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Register custom font as @font-face
    const registerFontFace = useCallback((familyName, fontFileUrl) => {
        if (!fontFileUrl || !familyName) return;
        const id = `custom-font-${familyName.replace(/\s+/g, '-').toLowerCase()}`;
        let style = document.getElementById(id);
        if (!style) {
            style = document.createElement('style');
            style.id = id;
            document.head.appendChild(style);
        }
        style.textContent = `@font-face { font-family: '${familyName}'; src: url('${fontFileUrl}') format('opentype'); font-display: swap; }`;
        console.log(`[Font] Registered @font-face: ${familyName}`);
    }, []);

    // Save: create or update
    const saveProject = useCallback(async () => {
        setSaving(true);
        try {
            const payload = {
                name: metadata.familyName || 'Untitled',
                styleName: metadata.styleName || 'Regular',
                canvasId: canvasId || null,
                metadata,
                glyphs,
                accents,
            };

            let result;
            if (currentProjectId) {
                // Update existing
                const res = await api.put(`/font-families/${currentProjectId}`, payload);
                result = res.data;
            } else {
                // Create new
                const res = await api.post('/font-families', payload);
                result = res.data;
                setCurrentProjectId(result.id);
            }

            setLastSaved(new Date());
            await loadSavedProjects();
            message.success('Font project saved!');

            // Poll for fontFileUrl (generated async on backend)
            const projectId = result.id || currentProjectId;
            if (projectId) {
                setTimeout(async () => {
                    try {
                        const check = await api.get(`/font-families/${projectId}`);
                        if (check.data?.fontFileUrl) {
                            registerFontFace(metadata.familyName || 'Untitled', check.data.fontFileUrl);
                        }
                    } catch { }
                }, 3000);
            }
        } catch (e) {
            console.error('Save error:', e);
            message.error('Failed to save project');
        } finally {
            setSaving(false);
        }
    }, [metadata, glyphs, accents, canvasId, currentProjectId, loadSavedProjects, registerFontFace]);

    // Load a specific project by ID
    const loadProject = useCallback(async (id) => {
        try {
            const res = await api.get(`/font-families/${id}`);
            const project = res.data;
            if (project.metadata) setMetadata(project.metadata);
            if (project.glyphs?.length) setGlyphs(project.glyphs);
            if (project.accents) setAccents(project.accents);
            setCurrentProjectId(project.id);
            setLastSaved(new Date(project.updated_at || project.created_at));
            setEditingGlyph(null);
            setActiveTab('library');

            // Register the font if it has a WOFF2 file
            if (project.fontFileUrl) {
                registerFontFace(project.metadata?.familyName || project.name || 'Untitled', project.fontFileUrl);
            }

            message.success(`Loaded "${project.name || 'font'}"`);
        } catch (e) {
            message.error('Failed to load project');
        }
    }, [registerFontFace]);

    // Delete a project
    const deleteProject = useCallback(async (id) => {
        try {
            await api.delete(`/font-families/${id}`);
            if (currentProjectId === id) setCurrentProjectId(null);
            await loadSavedProjects();
            message.info('Project deleted');
        } catch (e) {
            message.error('Failed to delete project');
        }
    }, [currentProjectId, loadSavedProjects]);

    // Auto-save every 60 seconds (only if project already exists in DB)
    useEffect(() => {
        if (!open || !currentProjectId) return;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(async () => {
            try {
                await api.put(`/font-families/${currentProjectId}`, {
                    name: metadata.familyName || 'Untitled',
                    styleName: metadata.styleName || 'Regular',
                    canvasId: canvasId || null,
                    metadata,
                    glyphs,
                    accents,
                });
                setLastSaved(new Date());
            } catch { }
        }, 60000);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    }, [open, metadata, glyphs, accents, canvasId, currentProjectId]);

    // Sentence preview state
    const [previewText, setPreviewText] = useState('Hello World');
    const [fontSize, setFontSize] = useState(72);
    const [letterSpacing, setLetterSpacing] = useState(0);
    const [lineHeight, setLineHeight] = useState(1.4);
    const [hoveredChar, setHoveredChar] = useState(null);

    // Sanitize path: ensure each contour starts with M, no duplicate Z's, no orphaned commands
    const sanitizePath = useCallback((path) => {
        if (!path || path.length === 0) return path;

        // Split path into contours at Z boundaries
        const contours = [];
        let current = [];
        for (const cmd of path) {
            if (cmd.type === 'Z') {
                if (current.length > 0) {
                    current.push({ type: 'Z' });
                    contours.push(current);
                    current = [];
                }
                // Skip duplicate Z's
            } else {
                current.push(cmd);
            }
        }
        // Last open contour (no Z at end)
        if (current.length > 0) {
            contours.push(current);
        }

        // Rebuild: ensure each contour starts with M
        const result = [];
        for (const contour of contours) {
            if (contour.length === 0) continue;
            // Skip degenerate contours (only Z, or only M)
            const realPoints = contour.filter(c => c.type !== 'Z' && c.x != null);
            if (realPoints.length === 0) continue;

            // Ensure first command is M
            if (contour[0].type !== 'M') {
                // Convert first command to M, preserving coordinates and cornerRadius
                const first = contour[0];
                contour[0] = { type: 'M', x: first.x, y: first.y, ...(first.cornerRadius ? { cornerRadius: first.cornerRadius } : {}) };
            }
            result.push(...contour);
        }
        return result;
    }, []);

    // Update a single glyph
    const handleGlyphChange = useCallback((updatedGlyph) => {
        const sanitized = { ...updatedGlyph, path: sanitizePath(updatedGlyph.path) };
        setGlyphs(prev => prev.map(g =>
            g.unicode === sanitized.unicode ? sanitized : g
        ));
        setEditingGlyph(sanitized);
    }, [sanitizePath]);

    // Open glyph editor
    const handleEditGlyph = useCallback((g) => {
        setEditingGlyph(g);
        setActiveTab('editor');
    }, []);

    // Export helpers
    const exportFont = useCallback(async (format) => {
        setExporting(true);
        try {
            const endpoint = format === 'woff2' ? '/glyphs/export/woff2'
                : format === 'woff' ? '/glyphs/export/woff'
                    : '/glyphs/build';

            const response = await api.post(endpoint, { metadata, glyphs }, { responseType: 'blob' });

            const ext = format === 'woff2' ? 'woff2' : format === 'woff' ? 'woff' : 'otf';
            const blob = new Blob([response.data]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${metadata.familyName.replace(/\s+/g, '-')}.${ext}`;
            a.click();
            URL.revokeObjectURL(url);
            message.success(`Exported as .${ext}`);
        } catch (err) {
            console.error('Export error:', err);
            message.error(`Export failed: ${err.message}`);
        } finally {
            setExporting(false);
        }
    }, [metadata, glyphs]);

    const exportGlyphTIFF = useCallback(async () => {
        if (!editingGlyph) return;
        setExporting(true);
        try {
            const response = await api.post('/glyphs/export/tiff', {
                path: editingGlyph.path,
                options: {
                    unitsPerEm: metadata.unitsPerEm,
                    ascender: metadata.ascender,
                },
            }, { responseType: 'blob' });

            const blob = new Blob([response.data], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `glyph-${editingGlyph.name}.svg`;
            a.click();
            URL.revokeObjectURL(url);
            message.success('Glyph exported as SVG');
        } catch (err) {
            console.error('Export error:', err);
            message.error('Failed to export glyph');
        } finally {
            setExporting(false);
        }
    }, [editingGlyph, metadata]);

    const exportMenuItems = [
        { key: 'otf', label: 'Download .OTF', onClick: () => exportFont('otf') },
        { key: 'woff', label: 'Download .WOFF', onClick: () => exportFont('woff') },
        { key: 'woff2', label: 'Download .WOFF2', onClick: () => exportFont('woff2') },
        ...(editingGlyph ? [{ key: 'svg', label: 'Export Glyph SVG', onClick: exportGlyphTIFF }] : []),
    ];

    // Metadata editor (inline in header)
    const metaField = (label, key, type = 'text') => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, color: '#484f58' }}>{label}</span>
            {type === 'number' ? (
                <InputNumber
                    size="small" value={metadata[key]}
                    onChange={v => setMetadata(prev => ({ ...prev, [key]: v }))}
                    style={{ width: 60, background: '#0d1117', border: '1px solid #30363d', fontSize: 11 }}
                />
            ) : (
                <Input
                    size="small" value={metadata[key]}
                    onChange={e => setMetadata(prev => ({ ...prev, [key]: e.target.value }))}
                    style={{ width: key === 'familyName' ? 140 : 80, background: '#0d1117', border: '1px solid #30363d', color: '#c9d1d9', fontSize: 11 }}
                />
            )}
        </div>
    );

    const tabItems = [
        {
            key: 'library',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><AppstoreOutlined /> Library</span>,
        },
        {
            key: 'preview',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><EyeOutlined /> Preview</span>,
        },
        ...(editingGlyph ? [{
            key: 'editor',
            label: <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><EditOutlined /> Edit: {editingGlyph.name}</span>,
        }] : []),
    ];

    const HEADER_HEIGHT = 64;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            placement="bottom"
            height="100%"
            closable={false}
            mask={true}
            maskClosable={true}
            keyboard={false}
            rootStyle={{
                top: HEADER_HEIGHT,
                height: `calc(100vh - ${HEADER_HEIGHT}px)`
            }}
            styles={{
                wrapper: { top: 0, height: '100%' },
                mask: { top: 0, height: '100%' },
                content: { borderTop: 'none', boxShadow: 'none', height: '100%' },
                header: { display: 'none' },
                body: { padding: 0, background: '#0d1117', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' },
            }}
            zIndex={1001}
        >
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 20px', borderBottom: '1px solid #30363d',
                background: '#161b22', flexShrink: 0,
            }}>
                <span style={{ color: '#e6edf3', fontSize: 14, fontWeight: 600 }}>Glyph Editor</span>

                <div style={{ width: 1, height: 20, background: '#30363d', margin: '0 4px' }} />

                {metaField('Family', 'familyName')}
                {metaField('Style', 'styleName')}
                {metaField('UPM', 'unitsPerEm', 'number')}

                <div style={{ width: 1, height: 20, background: '#30363d', margin: '0 4px' }} />

                {/* Inline Tabs */}
                <style>{`
                    .glyph-tabs.ant-segmented { background: #21262d; border-radius: 8px; border: 1px solid #30363d; }
                    .glyph-tabs .ant-segmented-item { color: #8b949e; font-size: 12px; }
                    .glyph-tabs .ant-segmented-item:hover { color: #c9d1d9; }
                    .glyph-tabs .ant-segmented-item-selected { color: #58a6ff !important; }
                    .glyph-tabs .ant-segmented-thumb { background: #161b22; border-radius: 6px; }
                    .glyph-tabs *, .glyph-tabs *::before, .glyph-tabs *::after { transition: none !important; animation: none !important; }
                `}</style>
                <Segmented
                    className="glyph-tabs"
                    value={activeTab}
                    onChange={setActiveTab}
                    options={tabItems.map(t => ({ label: t.label, value: t.key }))}
                    size="small"
                />

                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                    {lastSaved && (
                        <span style={{ fontSize: 10, color: '#484f58' }}>
                            Last saved: {lastSaved.toLocaleTimeString()}
                        </span>
                    )}
                    <Button
                        type="text"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={saveProject}
                        onMouseEnter={e => e.currentTarget.style.color = '#1f6feb'}
                        onMouseLeave={e => e.currentTarget.style.color = '#c9d1d9'}
                        style={{ color: '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                    >
                        Save
                    </Button>

                    <Dropdown
                        menu={{
                            items: savedProjects.length > 0
                                ? savedProjects.map(p => ({
                                    key: p.id,
                                    label: (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minWidth: 200 }}>
                                            <div>
                                                <div style={{ fontWeight: p.id === currentProjectId ? 600 : 400, color: p.id === currentProjectId ? '#58a6ff' : '#c9d1d9' }}>
                                                    {p.name || 'Untitled'}
                                                </div>
                                                <div style={{ fontSize: 10, color: '#484f58' }}>
                                                    {p.glyphs ? `${p.glyphs.filter(g => g.path?.length > 0).length} glyphs` : ''}
                                                    {p.updated_at ? ` · ${new Date(p.updated_at).toLocaleDateString()}` : ''}
                                                </div>
                                            </div>
                                            <DeleteOutlined
                                                style={{ color: '#f85149', fontSize: 12 }}
                                                onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }}
                                            />
                                        </div>
                                    ),
                                    onClick: () => loadProject(p.id),
                                }))
                                : [{ key: 'empty', label: 'No saved fonts yet', disabled: true }],
                        }}
                        trigger={['click']}
                    >
                        <Button
                            type="text"
                            icon={<FolderOpenOutlined />}
                            onMouseEnter={e => e.currentTarget.style.color = '#58a6ff'}
                            onMouseLeave={e => e.currentTarget.style.color = '#c9d1d9'}
                            style={{ color: '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                        >
                            Open
                        </Button>
                    </Dropdown>

                    <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                        <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            loading={exporting}
                            onMouseEnter={e => e.currentTarget.style.color = '#238636'}
                            onMouseLeave={e => e.currentTarget.style.color = '#c9d1d9'}
                            style={{ color: '#c9d1d9', fontWeight: 500, fontSize: 14, borderRadius: 10 }}
                        >
                            Export
                        </Button>
                    </Dropdown>
                </div>
            </div>


            {/* Content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {activeTab === 'library' && (
                    <GlyphLibraryView
                        glyphs={glyphs}
                        selectedGlyph={selectedGlyph}
                        onSelectGlyph={setSelectedGlyph}
                        onEditGlyph={handleEditGlyph}
                        searchText={searchText}
                        onSearchChange={setSearchText}
                        categoryFilter={categoryFilter}
                        onCategoryChange={setCategoryFilter}
                    />
                )}
                {activeTab === 'preview' && (
                    <SentencePreviewView
                        glyphs={glyphs}
                        metadata={metadata}
                        previewText={previewText}
                        onPreviewTextChange={setPreviewText}
                        fontSize={fontSize}
                        onFontSizeChange={setFontSize}
                        letterSpacing={letterSpacing}
                        onLetterSpacingChange={setLetterSpacing}
                        lineHeight={lineHeight}
                        onLineHeightChange={setLineHeight}
                        hoveredChar={hoveredChar}
                        onHoveredCharChange={setHoveredChar}
                    />
                )}
                {activeTab === 'editor' && editingGlyph && (
                    <GlyphBezierEditor
                        glyph={editingGlyph}
                        metadata={metadata}
                        onMetadataChange={setMetadata}
                        onGlyphChange={handleGlyphChange}
                        onBack={() => setActiveTab('library')}
                        accents={accents}
                        onAccentsChange={setAccents}
                        allGlyphs={glyphs}
                        onSwitchGlyph={handleEditGlyph}
                    />
                )}
            </div>
        </Drawer>
    );
};

export default GlyphEditorPanel;
